import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { RefreshCw, CheckCircle, Bell, ArrowRight } from "lucide-react";
import { ListenButton } from "./ListenButton";

export default function Resources() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || "en").split("-")[0];
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifiedId, setNotifiedId] = useState(null);

  // Scroll to a requested resource card when the page loads (or the hash changes)
  // with a #id, but only once the list has rendered so the element exists.
  useEffect(() => {
    if (loading) return;
    const raw = (location.hash || "").replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [loading, resources, location.hash]);

  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const response = await axios.get(`${backendUrl}/api/resources?lang=${lang}`);
        setResources(response.data);
      } catch (err) {
        console.error("Error loading resources:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [lang]);

  const curated = t("resources.curated", { returnObjects: true }) || [];

  return (
    <div className="py-12 space-y-24 md:space-y-32">

      {/* Page Header */}
      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("resources.eyebrow")}</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">{t("resources.heading")}</h1>
        <p className="font-serif text-base sm:text-lg text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">{t("resources.subtitle")}</p>
        <div className="flex justify-center pt-2">
          <ListenButton text={t("resources.listenText")} testId="resources-page-listen" idleKey="listen.toPage" />
        </div>
      </section>

      {/* Main Grid section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {loading ? (
          <div className="py-24 text-center text-secondary-custom flex flex-col items-center space-y-4">
            <RefreshCw className="w-8 h-8 animate-spin text-[var(--accent-bronze)]" />
            <span className="font-serif italic text-sm">{t("resources.opening")}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-16">
            {resources.map((res) => (
              <div key={res.id} id={res.id} data-testid={`resource-card-${res.id}`} className="group border-t border-subtle-custom pt-8 flex flex-col scroll-mt-24">
                <div className="flex items-center gap-3">
                  <span className="text-[9px] font-sans tracking-[0.25em] text-[var(--accent-bronze)] uppercase font-semibold">{res.type}</span>
                  <span className="w-px h-3 bg-subtle-custom" />
                  <span className="text-[9px] font-sans tracking-[0.2em] text-secondary-custom uppercase">{res.category}</span>
                </div>
                {res.coverImage && (
                  <img
                    src={res.coverImage}
                    alt={res.title}
                    data-testid={`resource-cover-${res.id}`}
                    className="mt-5 w-24 sm:w-28 h-auto object-contain border border-subtle-custom/60 shadow-sm"
                  />
                )}
                <h3 className="text-2xl md:text-3xl font-serif font-medium text-primary-custom leading-snug mt-5">{res.title}</h3>
                <div className="mt-4 max-w-md space-y-3">
                  {(res.description || "").split(/\n\n+/).map((para, i) => (
                    <p key={i} className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{para}</p>
                  ))}
                </div>
                <div className="mt-7 flex items-center gap-5">
                  {res.externalUrl ? (
                    <a
                      href={res.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`resource-cta-${res.id}`}
                      className="group/btn inline-flex items-center gap-2.5 text-[10px] font-sans uppercase tracking-[0.25em] text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300"
                    >
                      <span>{res.ctaLabel || "View"}</span>
                      <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </a>
                  ) : notifiedId === res.id ? (
                    <span data-testid={`resource-notify-msg-${res.id}`} className="inline-flex items-center gap-2 text-[11px] font-serif italic text-emerald-600 dark:text-emerald-400">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>{t("resources.notifyMsg")}</span>
                    </span>
                  ) : (
                    <button onClick={() => setNotifiedId(res.id)} data-testid={`resource-notify-btn-${res.id}`} className="group/btn inline-flex items-center gap-2.5 text-[10px] font-sans uppercase tracking-[0.25em] text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors duration-300">
                      <Bell className="w-3.5 h-3.5" strokeWidth={1.5} />
                      <span>{t("resources.notify")}</span>
                    </button>
                  )}
                  {res.status && (
                    <span className="ml-auto text-[9px] font-sans tracking-[0.25em] uppercase text-secondary-custom/60">{res.status}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* The Curated Collection */}
      <section className="bg-surface-custom py-24 transition-colors duration-500 border-t border-subtle-custom" data-testid="curated-collection">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-12">
          <div className="max-w-3xl space-y-4">
            <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("resources.curatedEyebrow")}</span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight text-primary-custom">{t("resources.curatedHeading")}</h2>
            <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("resources.curatedIntro")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10 pt-4">
            {curated.map((item, idx) => (
              <div key={idx} data-testid={`curated-category-${idx}`} className="group border-t border-subtle-custom pt-5">
                <h3 className="text-lg font-serif text-primary-custom group-hover:text-[var(--accent-bronze)] transition-colors duration-300">{item.name}</h3>
                <p className="font-serif text-xs sm:text-sm text-secondary-custom leading-relaxed font-light mt-2">{item.note}</p>
              </div>
            ))}
          </div>
          <p className="font-serif text-xs italic text-secondary-custom max-w-2xl">{t("resources.curatedNote")}</p>
        </div>
      </section>

    </div>
  );
}
